import { xCheck, xFindClosestIndex } from 'ml-spectra-processing/x';
import { meanFilter } from "./filter/mean_filter.js";
import { percentageFilter } from "./filter/percentage_filter.js";
import { applyLockMass } from "./ms/apply_lock_mass.js";
import { calculateBpc } from "./ms/calculate_bpc.js";
import { calculateEic } from "./ms/calculate_eic.js";
import { calculateForMF } from "./ms/calculate_for_mf.js";
import { calculateLength } from "./ms/calculate_length.js";
import { calculateTic } from "./ms/calculate_tic.js";
import { deconvolution } from "./ms/deconvolution.js";
import { merge } from "./ms/merge.js";
import { getPeaks } from "./peaks/get_peaks.js";
import { seriesFromArray } from "./series_from_array.js";
import { filter } from "./util/filter.js";
import { getClosestData } from "./util/get_closest_data.js";
import { getMzVsTimesMatrix } from "./util/get_mz_vs_times_matrix.js";
import { integrate } from "./util/integrate.js";
export class Chromatogram {
    times;
    series;
    meta;
    /**
     * Construct a new chromatogram.
     * @param times - Array of time points.
     * @param series - Dictionary of series.
     * @param options
     */
    constructor(times, series, options = {}) {
        this.meta = options.meta || {};
        this.series = {};
        xCheck(times);
        this.times = times;
        if (series) {
            for (const [name, value] of Object.entries(series)) {
                this.addSeries(name, value);
            }
        }
    }
    /**
     * Returns the number of time points.
     */
    get length() {
        return this.times.length;
    }
    /**
     * Find the series from its name.
     * @param seriesName - Name of the series.
     */
    getSeries(seriesName) {
        this.requiresSeries(seriesName);
        return this.series[seriesName];
    }
    /**
     * Find the series from its name.
     * Throws an error if it is not a 1D series.
     * @param seriesName - Name of the series.
     */
    getSeries1D(seriesName) {
        const series = this.getSeries(seriesName);
        if (!series.is1D()) {
            throw new Error(`series "${seriesName}" is not a 1D series`);
        }
        return series;
    }
    /**
     * Find the series from its name.
     * Throws an error if it is not a 2D series.
     * @param seriesName - Name of the series.
     */
    getSeries2D(seriesName) {
        const series = this.getSeries(seriesName);
        if (!series.is2D()) {
            throw new Error(`series "${seriesName}" is not a 2D series`);
        }
        return series;
    }
    /**
     * Returns the names of all series.
     */
    getSeriesNames() {
        return Object.keys(this.series);
    }
    /**
     * Returns whether the chromatogram has an "ms" series.
     */
    hasMass() {
        return this.hasSeries('ms');
    }
    /**
     * Delete a series.
     */
    deleteSeries(seriesName) {
        this.requiresSeries(seriesName);
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete this.series[seriesName];
        return this;
    }
    /**
     * Add a new series.
     * @param seriesName - Name of the series to add.
     * @param data - Series data.
     * @param options
     */
    addSeries(seriesName, data, options = {}) {
        if (this.hasSeries(seriesName) && !options.force) {
            throw new Error(`A series with name "${seriesName}" already exists`);
        }
        if (this.times.length !== data.length) {
            throw new Error(`The series size is not the same as the times size`);
        }
        const series = seriesFromArray(data, options);
        this.series[seriesName] = series;
        series.name = seriesName;
        return this;
    }
    /**
     * Returns whether the series exists.
     * @param seriesName - Name of the series to check.
     */
    hasSeries(seriesName) {
        return this.series[seriesName] !== undefined;
    }
    /**
     * Throws an error if the series does not exist.
     * @param seriesName - Name of the series to check.
     */
    requiresSeries(seriesName) {
        if (!this.hasSeries(seriesName)) {
            throw new Error(`The series "${seriesName}" does not exist`);
        }
    }
    /**
     * Returns the first time value.
     */
    get firstTime() {
        return this.times[0];
    }
    /**
     * Returns the last time value.
     */
    get lastTime() {
        return this.times[this.length - 1];
    }
    /**
     * Returns the time values.
     */
    getTimes() {
        return this.times;
    }
    /**
     * Assign the time values.
     * @param times - New time values.
     */
    setTimes(times) {
        if (times.length !== this.times.length) {
            throw new Error('New times must have the same length as the old ones');
        }
        this.times = times;
        return this;
    }
    /**
     * Modifies the time applying the conversion function.
     * @param conversionFunction
     */
    rescaleTime(conversionFunction) {
        this.times = this.times.map((time) => conversionFunction(time));
        return this;
    }
    /**
     * Will filter the entries based on the index and time.
     * @param callback
     * @param options
     */
    filter(callback, options) {
        return filter(this, callback, options);
    }
    /**
     * Apply the GSD peak picking algorithm.
     * @param options
     * @returns - List of GSD objects
     */
    getPeaks(options) {
        return getPeaks(this, options);
    }
    /**
     * Calculate the "tic" series from the "ms" one.
     */
    calculateTic(options = {}) {
        if (!this.hasSeries('tic') || options.force) {
            const tic = calculateTic(this);
            this.addSeries('tic', tic, { force: true });
        }
        return this;
    }
    /**
     * Calculate length (number of points of each related information) and save it in the "length" series.
     * @param seriesName - Name of the series to make calculation on. It must be a 2D series.
     * @param options
     */
    calculateLength(seriesName, options = {}) {
        if (!this.hasSeries('length') || options.force) {
            const length = calculateLength(this, seriesName);
            this.addSeries('length', length, { force: true });
        }
        return this;
    }
    /**
     * Calculate the base peak chromatogram (BPC) and save it in the "bpc" series.
     * @param options
     */
    calculateBpc(options = {}) {
        if (!this.hasSeries('bpc') || options.force) {
            const bpc = calculateBpc(this);
            this.addSeries('bpc', bpc, { force: true });
        }
        return this;
    }
    /**
     * Calculate the extracted ion chromatogram (EIC) and save it in a new series.
     * @param targetMass - Mass or masses for which to extract the spectrum.
     * @param options
     * @returns - The created series.
     */
    calculateEic(targetMass, options = {}) {
        const { slotWidth = 1 } = options;
        const { seriesName = `ms${String(targetMass)}±${slotWidth / 2 || 0.5}`, cache = false, } = options;
        if (cache && this.hasSeries(seriesName)) {
            return this.getSeries1D(seriesName);
        }
        const result = calculateEic(this, targetMass, options);
        this.addSeries(seriesName, result, options);
        return this.getSeries1D(seriesName);
    }
    /**
     * Calculate mass spectrum by filtering for a molecular formula.
     * @param targetMF - MF for which to extract the spectrum.
     * @param options
     * @returns - The created series.
     */
    async calculateForMF(targetMF, options = {}) {
        const { slotWidth = 1 } = options;
        const { seriesName = `${targetMF}(${options.ionizations || 'H+'})±${slotWidth / 2 || 0.5}${options.threshold ? `(${options.threshold})` : ''}`, cache = false, } = options;
        if (cache && this.hasSeries(seriesName)) {
            return this.getSeries1D(seriesName);
        }
        const result = await calculateForMF(this, targetMF, options);
        this.addSeries(seriesName, result, options);
        return this.getSeries1D(seriesName);
    }
    /**
     * Returns an array containing the integral of various ranges
     * @param ranges
     * @param options
     */
    integrate(ranges, options) {
        return integrate(this, ranges, options);
    }
    /**
     * Returns a mass spectrum corresponding to the merge of a range.
     * @param options
     */
    merge(options) {
        return merge(this, options);
    }
    /**
     * Returns the index of the closest real time to the specified one.
     * @param time - Retention time.
     * @returns - Time index.
     */
    getClosestTime(time) {
        return xFindClosestIndex(this.getTimes(), time);
    }
    /**
     * Returns the closest mass spectrum to a specific retention time
     * @param time - Retention time
     * @param options
     */
    getClosestData(time, options) {
        return getClosestData(this, time, options);
    }
    /**
     * Returns a copy of the chromatogram.
     */
    copy() {
        const json = this.toJSON();
        return fromJSON(json);
    }
    /**
     * Filter the given 2D series based on its median value.
     * @param seriesName - Name of the series to filter.
     * @param options
     */
    meanFilter(seriesName, options = {}) {
        const { seriesName: newSeriesName = 'msMean' } = options;
        if (this.hasSeries(newSeriesName) && !options.force) {
            throw new Error(`A series with name "${seriesName}" already exists`);
        }
        const newSeries = meanFilter(this, seriesName, options);
        this.series[newSeriesName] = newSeries;
        return newSeries;
    }
    /**
     * Filter the given 2D series based on the percentage of the highest value
     * @param seriesName - Name of the series to filter.
     * @param options
     */
    percentageFilter(seriesName, options = {}) {
        const { seriesName: newSeriesName = 'msPercentage' } = options;
        if (this.hasSeries(newSeriesName) && !options.force) {
            throw new Error(`A series with name "${seriesName}" already exists`);
        }
        const newSeries = percentageFilter(this, seriesName, options);
        this.series[newSeriesName] = newSeries;
        return newSeries;
    }
    /**
     * Recalculates series for GC/MS with lock mass.
     * @param mfs - Reference molecular formula(s).
     * @param options
     */
    applyLockMass(mfs, options) {
        return applyLockMass(this, mfs, options);
    }
    /**
     * Return the submatrix, times, and mass x axis for each range
     */
    getMzVsTimesMatrix(range) {
        return getMzVsTimesMatrix(this, range);
    }
    /**
     * Performing non-negative matrix factorization solving
     * argmin_(A >= 0, S >= 0) 1 / 2 * ||Y - AS||_2^2 + lambda * ||S||_1
     */
    deconvolution(options) {
        return deconvolution(this, options);
    }
    /**
     * Returns a serializable representation of the chromatogram.
     */
    toJSON() {
        return {
            times: Array.from(this.times),
            series: Object.fromEntries(Object.entries(this.series).map(([name, series]) => [
                name,
                series.toJSON(),
            ])),
        };
    }
}
/**
 * Create a Chromatogram from a JSON representation.
 * @param json
 */
export function fromJSON(json) {
    const { series, times, meta = {} } = json;
    const chromatogram = new Chromatogram(times, undefined, { meta });
    if (Array.isArray(series)) {
        for (const serie of series) {
            chromatogram.addSeries(serie.name, serie.data, {
                meta: serie.meta,
            });
        }
    }
    else {
        for (const key of Object.keys(series)) {
            chromatogram.addSeries(key, series[key].data, {
                meta: series[key].meta,
            });
        }
    }
    return chromatogram;
}
//# sourceMappingURL=chromatogram.js.map